import { CompensationReviewRecommendationStatus } from "@prisma/client";
import { mapSeries } from "bluebird";
import { ensure } from "~/lib/ensure";
import { BusinessLogicError } from "~/lib/errors/businessLogicError";
import { trackCompensationReviewRecommandationUpdated } from "~/lib/external/segment/server/events";
import { compact } from "~/lib/lodash";
import { convertCurrency } from "~/lib/money";
import { assertNotNil } from "~/lib/utils";
import { type UpdateRecommendationInput } from "~/pages/api/compensation-review/update-recommendation";
import { externalEmployeeSelectForDisplay } from "~/services/compensation-review/campaigns/admin/fetchCompensationReviewEmployees";
import {
  type CompensationReviewContext,
  isOffCycleReviewContext,
} from "~/services/compensation-review/compensationReviewContext";
import { prismaCompensationReviewScope } from "~/services/compensation-review/compensationReviewScope";
import { getCompensationReviewBudget } from "~/services/compensation-review/shared/compensationReviewBudget";
import { computeDateProration } from "~/services/compensation-review/shared/computeDateProration";
import { submitRecommendation } from "~/services/compensation-review/shared/submitRecommendation";
import {
  selectCompensationReviewCurrency,
  transformCompensationReviewCurrency,
} from "~/services/compensation-review/shared/transformCompensationReviewCurrency";
import { selectSalaryRangeForAutocomplete } from "~/services/salary-bands/access/fetchSalaryRangesForAutocomplete";

export const updateRecommendation = async (ctx: CompensationReviewContext, params: UpdateRecommendationInput) => {
  ensure(() => ctx.permissions.canReview);

  const recommendation = await fetchRecommendationForUpdate(ctx, {
    recommendationId: params.recommendationId,
    reviewerId: assertNotNil(ctx.authenticatedReviewer).id,
  });

  const targetRangeAfterPromotion = params.targetRangeAfterPromotionId
    ? await ctx.prisma.salaryRange.findUniqueOrThrow({
        where: { id: params.targetRangeAfterPromotionId },
        select: selectSalaryRangeForAutocomplete,
      })
    : null;

  const { employee } = recommendation;

  const employeeAdjustments = await ctx.prisma.compensationReviewAdjustment.findMany({
    where: {
      employeeId: employee.id,
      AND: [prismaCompensationReviewScope(ctx.scope)],
    },
  });

  if (employeeAdjustments.length !== params.adjustments.length) {
    throw new BusinessLogicError("Some adjustments are not found");
  }

  const sourceAdjustments = await ctx.prisma.compensationReviewAdjustment.findMany({
    where: {
      id: { in: params.adjustments.map((adjustment) => adjustment.id) },
      AND: [prismaCompensationReviewScope(ctx.scope)],
      OR: [{ recommendationId: recommendation.id }, { employeeId: employee.id }],
    },
  });

  await ctx.prisma.compensationReviewRecommendation.update({
    where: { id: recommendation.id },
    data: {
      touchedAt: new Date(),
      comment: params.comment,

      ...(ctx.permissions.canUpdatePromotions && {
        isPromoted: params.isPromoted,
        targetRangeAfterPromotionId: targetRangeAfterPromotion?.id ?? null,
      }),
    },
  });

  const recommendationsToUpdate = params.adjustments
    .map((adjustment) => {
      const sourceAdjustment = assertNotNil(sourceAdjustments.find((item) => item.id === adjustment.id));
      const budget = getCompensationReviewBudget(ctx, sourceAdjustment.budgetId);

      return {
        ...adjustment,
        budget,
        sourceAdjustment,
      };
    })
    .filter((adjustment) => {
      return adjustment.sourceAdjustment.recommendationId === recommendation.id;
    });

  const adjustments = await mapSeries(recommendationsToUpdate, async (adjustment) => {
    const convertedSubmittedAmount = convertCurrency(
      adjustment.submittedAmount,
      employee.currency,
      ctx.parameters.currency
    );

    const { proratedAmount: proratedSubmittedAmount } = computeDateProration({
      amount: convertedSubmittedAmount,
      prorationStartDate: adjustment.budget.prorationStartDate,
      effectiveDate: employee.effectiveDate,
    });

    return ctx.prisma.compensationReviewAdjustment.update({
      where: { id: adjustment.id },
      data: {
        submittedAmount: adjustment.submittedAmount,
        convertedSubmittedAmount,
        proratedSubmittedAmount,
      },
    });
  });

  // @todo(bast): This bypasses the rules
  if (isOffCycleReviewContext(ctx)) {
    await submitRecommendation(ctx, {
      recommendation,
      adjustments,
      recommendationInput: {
        isPromoted: params.isPromoted,
        targetRangeAfterPromotion,
      },
    });
  }

  await trackCompensationReviewRecommandationUpdated(ctx, {
    scope: ctx.scope,
    recommendationId: recommendation.id,
  });
};

export const fetchRecommendationForUpdate = async (
  ctx: CompensationReviewContext,
  params: {
    recommendationId: number;
    reviewerId?: number;
  }
) => {
  const recommendation = await ctx.prisma.compensationReviewRecommendation.findFirstOrThrow({
    where: {
      id: params.recommendationId,
      status: {
        in: [
          CompensationReviewRecommendationStatus.PENDING_FOR_REVIEW,
          CompensationReviewRecommendationStatus.SUBMITTED,
        ],
      },
      AND: compact([prismaCompensationReviewScope(ctx.scope), params.reviewerId && { reviewerId: params.reviewerId }]),
    },
    select: {
      id: true,
      reviewerId: true,
      status: true,
      comment: true,
      position: true,
      isPromoted: true,
      targetRangeAfterPromotion: {
        select: selectSalaryRangeForAutocomplete,
      },
      employee: {
        select: {
          id: true,
          isPromoted: true,
          effectiveDate: true,
          baseSalary: true,
          variablePay: true,
          onTargetEarnings: true,
          fteCoefficient: true,
          currency: { select: selectCompensationReviewCurrency },
          adjustments: true,
          eligibilities: {
            select: {
              budgetId: true,
            },
          },
          recommendations: {
            select: {
              id: true,
              status: true,
              position: true,
              reviewer: {
                select: {
                  id: true,
                  externalEmployeeId: true,
                  externalEmployee: {
                    select: {
                      userPermissions: {
                        select: {
                          user: {
                            select: {
                              id: true,
                              email: true,
                              firstName: true,
                              lastName: true,
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              isListedToReviewer: true,
            },
          },
          targetRangeAfterPromotion: {
            select: {
              min: true,
              max: true,
              midpoint: true,
              band: { select: { measure: true, currency: true } },
            },
          },
          externalEmployee: {
            select: {
              ...externalEmployeeSelectForDisplay,
              liveSalaryRangeEmployee: {
                select: {
                  range: {
                    select: {
                      min: true,
                      max: true,
                      midpoint: true,
                      band: { select: { measure: true, currency: true } },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });

  return {
    ...recommendation,
    employee: {
      ...recommendation.employee,
      currency: transformCompensationReviewCurrency(recommendation.employee.currency),
      salaryRangeEmployee: recommendation.employee.externalEmployee.liveSalaryRangeEmployee ?? null,
    },
  };
};
